import React, { useState, useEffect, useCallback } from 'react';
import api from '../../services/api';
import { format } from 'date-fns';
import { FiCalendar, FiBarChart2, FiFileText, FiList, FiInbox, FiAlertCircle, FiCheckCircle } from 'react-icons/fi';
import MyLeaveRequestItem from './MyLeaveRequestItem.jsx';
import './LeaveRequest.css';

const LeaveRequest = () => {
  const [formData, setFormData] = useState({
    leaveType: 'sick',
    leaveCount: 1,
    leaveDates: [],
    reason: ''
  });
  const [selectedDates, setSelectedDates] = useState([]);
  const [dateInputMode, setDateInputMode] = useState('count'); // 'count' or 'dates'
  const [myRequests, setMyRequests] = useState([]);
  const [myStats, setMyStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const fetchMyRequests = useCallback(async () => {
    try {
      const response = await api.get('/leaves/my-requests');
      setMyRequests(response.data.leaves);
    } catch (err) {
      console.error('Error fetching leave requests:', err);
    }
  }, []);

  const fetchMyStats = useCallback(async () => {
    try {
      const response = await api.get('/leaves/my-stats');
      setMyStats(response.data.stats);
    } catch (err) {
      console.error('Error fetching leave stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchMyRequests();
    fetchMyStats();
  }, [fetchMyRequests, fetchMyStats]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        leaveType: formData.leaveType,
        reason: formData.reason
      };

      if (dateInputMode === 'dates' && selectedDates.length > 0) {
        payload.leaveDates = selectedDates;
        payload.leaveCount = selectedDates.length;
      } else {
        payload.leaveCount = formData.leaveCount;
        payload.leaveDates = [];
      }

      await api.post('/leaves', payload);

      setSuccess('Leave request submitted successfully!');
      setFormData({ leaveType: 'sick', leaveCount: 1, leaveDates: [], reason: '' });
      setSelectedDates([]);
      fetchMyRequests();
      fetchMyStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelection = (dateStr) => {
    const dates = [...selectedDates];
    const index = dates.indexOf(dateStr);
    
    if (index > -1) {
      dates.splice(index, 1);
    } else {
      dates.push(dateStr);
    }
    
    dates.sort();
    setSelectedDates(dates);
    setFormData({ ...formData, leaveDates: dates, leaveCount: dates.length });
  };

  const handleDateInputChange = (e) => {
    const date = e.target.value;
    if (date) {
      handleDateSelection(date);
      e.target.value = '';
    }
  };

  const removeDateFromSelection = (dateStr) => {
    const dates = selectedDates.filter(d => d !== dateStr);
    setSelectedDates(dates);
    setFormData({ ...formData, leaveDates: dates, leaveCount: dates.length });
  };

  const handleDelete = useCallback(async (leaveId) => {
    if (!window.confirm('Are you sure you want to delete this leave request?')) return;

    try {
      await api.delete(`/leaves/${leaveId}`);
      setSuccess('Leave request deleted successfully');
      fetchMyRequests();
      fetchMyStats();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete leave request');
    }
  }, [fetchMyRequests, fetchMyStats]);

  return (
    <div className="leave-request-container">
      <div className="leave-header">
        <div className="leave-header-icon"><FiCalendar /></div>
        <div>
          <h1>Leave Management</h1>
          <p className="leave-header-subtitle">Track your leave balance and manage your requests</p>
        </div>
      </div>

      {/* Leave Statistics */}
      {myStats && (
        <div className="leave-stats-card">
          <h2><FiBarChart2 /> My Leave Statistics</h2>
          <div className="stats-grid">
            <div className="stat-item">
              <div className="stat-icon"><FiCalendar /></div>
              <div className="stat-info">
                <span className="stat-label">Joined On</span>
                <span className="stat-value stat-value-sm">{new Date(myStats.joiningDate).toLocaleDateString()}</span>
              </div>
            </div>
            <div className="stat-item highlight">
              <div className="stat-icon"><FiBarChart2 /></div>
              <div className="stat-info">
                <span className="stat-label">Total Taken</span>
                <span className="stat-value">{myStats.totalLeaves}</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">🤒</div>
              <div className="stat-info">
                <span className="stat-label">Sick</span>
                <span className="stat-value">{myStats.sick}</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">🏖️</div>
              <div className="stat-info">
                <span className="stat-label">Casual</span>
                <span className="stat-value">{myStats.casual}</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">🚨</div>
              <div className="stat-info">
                <span className="stat-label">Emergency</span>
                <span className="stat-value">{myStats.emergency}</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">📢</div>
              <div className="stat-info">
                <span className="stat-label">Informed</span>
                <span className="stat-value">{myStats.informed}</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">⚠️</div>
              <div className="stat-info">
                <span className="stat-label">Uninformed</span>
                <span className="stat-value">{myStats.uninformed}</span>
              </div>
            </div>
            <div className="stat-item">
              <div className="stat-icon">📝</div>
              <div className="stat-info">
                <span className="stat-label">Other</span>
                <span className="stat-value">{myStats.other}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Submit Leave Request Form */}
      <div className="leave-form-card">
        <h2><FiFileText /> Submit Leave Request</h2>
        {error && <div className="alert alert-error"><FiAlertCircle /> {error}</div>}
        {success && <div className="alert alert-success"><FiCheckCircle /> {success}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Leave Type *</label>
            <select
              value={formData.leaveType}
              onChange={(e) => setFormData({ ...formData, leaveType: e.target.value })}
              required
            >
              <option value="sick">🤒 Sick Leave</option>
              <option value="casual">🏖️ Casual Leave</option>
              <option value="emergency">🚨 Emergency Leave</option>
              <option value="informed">📢 Informed Leave</option>
              <option value="uninformed">⚠️ Uninformed Leave</option>
              <option value="other">📝 Other</option>
            </select>
          </div>

          {/* Date Selection Mode Toggle */}
          <div className="form-group">
            <label>Leave Selection Method</label>
            <div className="date-mode-toggle">
              <button
                type="button"
                className={`mode-btn ${dateInputMode === 'count' ? 'active' : ''}`}
                onClick={() => {
                  setDateInputMode('count');
                  setSelectedDates([]);
                }}
              >
                📅 Number of Days
              </button>
              <button
                type="button"
                className={`mode-btn ${dateInputMode === 'dates' ? 'active' : ''}`}
                onClick={() => {
                  setDateInputMode('dates');
                  setFormData({ ...formData, leaveCount: 0 });
                }}
              >
                🗓️ Select Specific Dates
              </button>
            </div>
          </div>

          {/* Number of Days Input (Old Method) */}
          {dateInputMode === 'count' && (
            <div className="form-group">
              <label>Number of Days *</label>
              <input
                type="number"
                min="1"
                max="365"
                value={formData.leaveCount}
                onChange={(e) => setFormData({ ...formData, leaveCount: parseInt(e.target.value) })}
                required
              />
            </div>
          )}

          {/* Date Picker (New Method) */}
          {dateInputMode === 'dates' && (
            <div className="form-group">
              <label>Select Leave Dates *</label>
              <input
                type="date"
                className="date-picker-input"
                onChange={handleDateInputChange}
                min={new Date().toISOString().split('T')[0]}
              />
              
              {selectedDates.length > 0 && (
                <div className="selected-dates-container">
                  <p className="selected-count">Selected: {selectedDates.length} day{selectedDates.length > 1 ? 's' : ''}</p>
                  <div className="dates-grid">
                    {selectedDates.map(date => (
                      <div key={date} className="date-chip">
                        <span>{format(new Date(date), 'MMM dd, yyyy')}</span>
                        <button
                          type="button"
                          className="remove-date-btn"
                          onClick={() => removeDateFromSelection(date)}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedDates.length === 0 && (
                <p className="hint-text">Click on the date picker above to select your leave dates</p>
              )}
            </div>
          )}

          <div className="form-group">
            <label>Reason *</label>
            <textarea
              rows="4"
              value={formData.reason}
              onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
              placeholder="Please provide a reason for your leave..."
              required
            />
          </div>

          <button type="submit" className="btn-submit" disabled={loading || (dateInputMode === 'dates' && selectedDates.length === 0)}>
            {loading ? 'Submitting...' : 'Submit Leave Request'}
          </button>
        </form>
      </div>

      {/* My Leave Requests */}
      <div className="leave-requests-card">
        <h2><FiList /> My Leave Requests</h2>
        {myRequests.length === 0 ? (
          <div className="no-data">
            <FiInbox className="no-data-icon" />
            <p>No leave requests found</p>
          </div>
        ) : (
          <div className="requests-list">
            {myRequests.map((leave) => (
              <MyLeaveRequestItem key={leave._id} leave={leave} onDelete={handleDelete} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveRequest;
